import { Box, Flex, Table, Tbody, Thead, Tr } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import Pagination from '../../Pagination';
import { SETTINGS_BORDER_COLOR, SETTINGS_CONTENT_MAX_WIDTH } from './constants';

/** Rows per page for a subtable before its pagination footer appears. */
export const SETTINGS_SUBTABLE_PAGE_SIZE = 10;

export interface SettingsSubTableProps {
    /** Header cells, normally SettingsTh elements. */
    head: ReactNode;
    children: ReactNode;
    /**
     * Drop the outer border and radius, for a subtable that already sits inside
     * a SettingsCard with `isFlush`.
     */
    isEmbedded?: boolean;
    /**
     * Keep the border and radius but drop the centred content-column width, for
     * a subtable inset inside a SettingsCard alongside other rows, so it reads
     * as its own panel rather than as the card's whole body.
     */
    isInset?: boolean;
    /**
     * Pagination state, normally from useSubTablePagination. The footer only
     * renders when there is more than one page.
     */
    currentPage?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    /**
     * Summary line pinned to the bottom-left of the table, inside its border —
     * coverage counts and the like. Renders the footer bar even when there is
     * only one page.
     */
    footerLeft?: ReactNode;
}

/**
 * A table nested inside a settings page rather than being the page itself: the
 * per-user permissions matrix, the invoicing markup/discount/flag rows, and so
 * on.
 *
 * Unlike SettingsTable, which runs full bleed and owns the viewport height,
 * this one sits in the standard section column (SETTINGS_CONTENT_MAX_WIDTH,
 * centered) and is only as tall as its rows. It keeps the grey uppercase header
 * but draws horizontal row splits only — no vertical column dividers — so a
 * short embedded table reads as a list rather than a grid.
 *
 * Long lists paginate at the bottom with the same Pagination control the
 * full-page tables use, pinned inside the table's border.
 */
const SettingsSubTable = ({
    head,
    children,
    isEmbedded = false,
    isInset = false,
    currentPage,
    totalPages = 1,
    onPageChange,
    footerLeft,
}: SettingsSubTableProps) => {
    const showPagination =
        typeof currentPage === 'number' && !!onPageChange && totalPages > 1;
    const showFooter = showPagination || !!footerLeft;

    return (
        <Box
            w="100%"
            maxW={isEmbedded || isInset ? undefined : SETTINGS_CONTENT_MAX_WIDTH}
            mx={isEmbedded || isInset ? undefined : 'auto'}
            bg="white"
            borderWidth={isEmbedded ? 0 : '1px'}
            borderColor={SETTINGS_BORDER_COLOR}
            borderRadius={isEmbedded ? 0 : 'lg'}
            overflow="hidden"
        >
            <Box
                overflowX="auto"
                sx={{
                    '& th': {
                        bg: 'gray.50',
                        borderBottomWidth: '1px',
                        borderBottomColor: 'var(--chakra-colors-gray-200)',
                    },
                    // Row splits only: nothing draws a vertical rule between
                    // columns, and the last row leans on the container border.
                    '& tbody tr:last-of-type td': {
                        borderBottomWidth: 0,
                    },
                    '& th:first-of-type, & td:first-of-type': { pl: 5 },
                    '& th:last-of-type, & td:last-of-type': { pr: 5 },
                    '&::-webkit-scrollbar': { height: '8px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#cbd5e0',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 transparent',
                }}
            >
                <Table variant="simple" size="sm" w="100%">
                    <Thead>
                        <Tr bg="gray.50">{head}</Tr>
                    </Thead>
                    <Tbody>{children}</Tbody>
                </Table>
            </Box>

            {showFooter && (
                <Flex
                    align="center"
                    gap={3}
                    minH="40px"
                    px={5}
                    py={1.5}
                    borderTopWidth="1px"
                    borderColor={SETTINGS_BORDER_COLOR}
                >
                    <Box flex={1} minW={0}>
                        {footerLeft}
                    </Box>
                    {showPagination && (
                        <Box flexShrink={0}>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={onPageChange}
                                py={0}
                            />
                        </Box>
                    )}
                    {/* Mirrors the summary column so the pager stays centred. */}
                    {showPagination && <Box flex={1} minW={0} />}
                </Flex>
            )}
        </Box>
    );
};

/**
 * Client-side paging for a subtable's rows. Everything is already in memory in
 * these sections, so this just slices; it also pulls the page back in range
 * when rows are removed off the end of the list.
 */
export const useSubTablePagination = <T,>(
    items: Array<T>,
    pageSize: number = SETTINGS_SUBTABLE_PAGE_SIZE,
) => {
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

    useEffect(() => {
        setCurrentPage((prev) => (prev > totalPages ? totalPages : prev));
    }, [totalPages]);

    const pageItems = useMemo(() => {
        const page = Math.min(currentPage, totalPages);
        const start = (page - 1) * pageSize;
        return items.slice(start, start + pageSize);
    }, [items, currentPage, totalPages, pageSize]);

    return { currentPage, setCurrentPage, totalPages, pageItems };
};

export default SettingsSubTable;
